export interface SelectionState {
    type: SelectionType
    mode: SelectionMode
    freeSelection: FreeSelection
    savedFreeSelections: FreeSelectionsForCase
    originalSavedFreeSelections: FreeSelectionsForCase
    hierarchicalSelection: HierarchicalSelection
    savedHierarchicalSelections: HierarchicalSelectionsForCase
    originalSavedHierarchicalSelections: HierarchicalSelectionsForCase
    displayedOnScatterPlot: SelectionBase[]
    unsupervisedSelection: UnsupervisedSelection
    savedUnsupervisedSelections: HierarchicalSelectionsForCase
    resolution: number
}

export enum SelectionType {
    Free,
    Hierarchical,
    Unsupervised,
}

export enum SelectionMode {
    Add,
    Subtract,
}

export interface SelectionBase {
    name: string | undefined
    modified: boolean
    points: Set<number>
    segSource: string | undefined
    annotationVisible: boolean
    annotationColor: string // hex string
    annotationOpacity: number // 0..1
}

export interface FreeSelection extends SelectionBase {
    type: SelectionType.Free
}

export interface HierarchicalSelectionNode extends SelectionBase {
    children: HierarchicalSelectionNode[] | undefined
}
export interface UnsupervisedSelectionNode extends SelectionBase {
    children: UnsupervisedSelectionNode[] | undefined
}

export interface HierarchicalSelectionRoot {
    name: string | undefined
    modified: boolean
    type: SelectionType.Hierarchical | SelectionType.Unsupervised
    children: HierarchicalSelectionNode[] | undefined
    segSource: string | undefined
}

export interface UnsupervisedSelectionRoot {
    name: string | undefined
    modified: boolean
    type: SelectionType.Unsupervised | SelectionType.Hierarchical
    children: UnsupervisedSelectionNode[] | undefined
    segSource: string | undefined
}

export interface SelectionLayerInfo extends SelectionBase {
    type: SelectionType
    rootName: string | undefined
}

export interface HierarchicalSelection extends HierarchicalSelectionRoot {
    selectedHierarchyName: string | undefined
    unsavedLevel: SelectionBase
}

export interface UnsupervisedSelection extends UnsupervisedSelectionRoot {
    selectedUnsupervisedName: string | undefined
    unsavedLevel: SelectionBase
}

export type FreeSelectionsForCase = Map<string, FreeSelection>
export type HierarchicalSelectionsForCase = Map<string, HierarchicalSelectionRoot>
export type UnsupervisedSelectionsForCase = Map<string, UnsupervisedSelectionRoot>

export type SelectionsForCaseList = SelectionBase[]

export interface SelectionTreeItem {
    level: number
    name: string | undefined
    size: number
}

export const findSelectionNodeInRoot = (
    selection: HierarchicalSelectionRoot,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNode(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

const findSelectionNodeInNode = (
    selection: HierarchicalSelectionNode,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.name === name) {
        return selection
    }
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNode(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

export const findParentSelectionNodeInRoot = (
    selection: HierarchicalSelectionRoot,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            if (child.name === name) {
                // do not return root
                return null
            }
            const parent = findParentSelectionNodeInNode(child, name)
            if (parent) {
                return parent
            }
        }
    }
    return null
}

const findParentSelectionNodeInNode = (
    selection: HierarchicalSelectionNode,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            if (child.name === name) {
                return selection
            }
            const parent = findParentSelectionNodeInNode(child, name)
            if (parent) {
                return parent
            }
        }
    }
    return null
}

export const getAllChildrenSelectionTreeItems = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode,
    level?: number
): SelectionTreeItem[] => {
    let result: SelectionTreeItem[] = []
    level = level ?? 1
    for (const child of selection.children ?? []) {
        if (child.name) {
            result.push({ level, name: child.name, size: child.points.size })
        }
        result = result.concat(getAllChildrenSelectionTreeItems(child, level + 1))
    }
    return result
}

export const getSelectionTreeItemLevel = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode,
    name: string,
    level?: number
): number => {
    level = level ?? 1
    for (const child of selection.children ?? []) {
        if (child.name == name) {
            return level
        }
        return getSelectionTreeItemLevel(child, name, level + 1)
    }
    return 0
}

export const getAllChildrenPointsInRootOrNode = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode
): Set<number> => {
    const points = new Set<number>()
    if (selection.children) {
        for (const child of selection.children) {
            const childPoints = getAllOwnAndChildrenPointsInNode(child)
            for (const point of childPoints) {
                points.add(point)
            }
        }
    }
    return points
}

export const getAllOwnPointsInNode = (selection: HierarchicalSelectionNode): Set<number> => {
    const points = new Set<number>()
    if (selection.points) {
        for (const point of selection.points) {
            points.add(point)
        }
    }
    return points
}

export const getAllOwnAndChildrenPointsInNode = (
    selection: HierarchicalSelectionNode
): Set<number> => {
    const points = new Set<number>()
    if (selection.points) {
        for (const point of selection.points) {
            points.add(point)
        }
    }
    if (selection.children) {
        for (const child of selection.children) {
            const childPoints = getAllOwnAndChildrenPointsInNode(child)
            for (const point of childPoints) {
                points.add(point)
            }
        }
    }
    return points
}

export const getAllChildren = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode
): HierarchicalSelectionNode[] => {
    if (selection.children) {
        return selection.children.reduce((children, child) => {
            const newChildren = getAllChildren(child)
            if (child) {
                newChildren.push(child)
            }
            return children.concat(newChildren)
        }, [] as HierarchicalSelectionNode[])
    }
    return []
}

export const addChildToSelectionRoot = (
    selectionRoot: HierarchicalSelectionRoot,
    newChild: HierarchicalSelectionNode,
    targetName: string | undefined
): boolean => {
    if (!targetName) {
        if (!selectionRoot.children) {
            selectionRoot.children = []
        }
        selectionRoot.children.push(newChild)
        return true
    }
    if (selectionRoot.children) {
        for (const child of selectionRoot.children) {
            if (addChildToSelectionNode(child, newChild, targetName)) {
                return true
            }
        }
    }
    return false
}

const addChildToSelectionNode = (
    selectionNode: HierarchicalSelectionNode,
    newChild: HierarchicalSelectionNode,
    targetName: string
): boolean => {
    if (selectionNode.name === targetName) {
        if (!selectionNode.children) {
            selectionNode.children = []
        }
        selectionNode.children.push(newChild)
        return true
    }
    if (selectionNode.children) {
        for (const child of selectionNode.children) {
            if (addChildToSelectionNode(child, newChild, targetName)) {
                return true
            }
        }
    }
    return false
}

export const removeChildrenFromSelectionRoot = (
    selectionRoot: HierarchicalSelectionRoot,
    name: string
): void => {
    if (selectionRoot.children) {
        selectionRoot.children = selectionRoot.children.filter(child => {
            if (child.name === name) {
                return false
            }
            removeChildrenFromSelectionNode(child, name)
            return true
        })
    }
}

const removeChildrenFromSelectionNode = (
    selectionNode: HierarchicalSelectionNode,
    name: string
): void => {
    if (selectionNode.children) {
        const copiedNode = [...selectionNode.children]
        const children: any = []

        copiedNode.forEach(child => {
            if (child.name === name) {
                children.push(...Array.from(child.children ?? []))
            } else {
                removeChildrenFromSelectionNode(child, name)
                children.push(child)
            }
        })
        selectionNode.children = children
    }
}

export const removeChildrenFromSelectionRootRecursively = (
    selectionRoot: HierarchicalSelectionRoot,
    name: string
): void => {
    if (selectionRoot.children) {
        selectionRoot.children = selectionRoot.children.filter(child => {
            if (child.name === name) {
                return false
            }
            removeChildrenFromSelectionNodeRecursively(child, name)
            return true
        })
    }
}

const removeChildrenFromSelectionNodeRecursively = (
    selectionNode: HierarchicalSelectionNode,
    name: string
): void => {
    if (selectionNode.children) {
        selectionNode.children = selectionNode.children.filter(child => {
            if (child.name === name) {
                return false
            }
            removeChildrenFromSelectionNodeRecursively(child, name)
            return true
        })
    }
}
